import { FC } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Typography, Divider, Box } from '@material-ui/core';
import useStore from '@hooks/useStore';
import { formattedAmount } from '@utils/format';
import { PaymentLinkStatus, TicketStatus } from '@constants/paymentLink';
import { useStyles } from './styles';

const getTicketStatus = (status: string): TicketStatus => {
  switch (status) {
    case PaymentLinkStatus.ACTIVE:
      return TicketStatus.PENDING;
    case PaymentLinkStatus.CLOSED:
      return TicketStatus.PAID;
    case PaymentLinkStatus.DELETED:
    case PaymentLinkStatus.ERROR:
      return TicketStatus.ERROR;
    case PaymentLinkStatus.SUBSCRIBED:
      return TicketStatus.SUBSCRIBED;
    case PaymentLinkStatus.SUBSCRIPTION_CANCELED:
      return TicketStatus.SUBSCRIPTION_CANCELED;
    case PaymentLinkStatus.PENDING_ACTIVATION:
      return TicketStatus.PENDING_ACTIVATION;
    default:
      return TicketStatus.UNKNOWN;
  }
};

const OrderSummary: FC = () => {
  const { t } = useTranslation();
  const { links } = useStore();
  const classes = useStyles();

  const { linkItems, currency, total, title, status } = links.paymentLink;
  const { businessName } = links.accountInfo;
  const ticketStatus = getTicketStatus(status);

  return (
    <div className={classes.root}>
      {ticketStatus !== TicketStatus.UNKNOWN && (
        <Typography variant="body2" className={classes.status}>
          {t(`checkoutScreen.ticket.status.${ticketStatus}`)}
        </Typography>
      )}

      <Box mb={3}>
        <Typography variant="h6" className={classes.title}>
          {t('checkoutScreen.ticket.for')}
        </Typography>
        <Typography variant="body1" className={classes.businessName}>
          {businessName}
        </Typography>
        {title && (
          <Typography variant="body2" color="textSecondary" className={classes.description}>
            {title}
          </Typography>
        )}
      </Box>

      <Typography variant="h6" className={classes.title}>
        {t('checkoutScreen.ticket.details')}
      </Typography>

      <div className={classes.items}>
        {linkItems.map((item, index) => (
          <div key={index} className={classes.item}>
            <div className={classes.itemInfo}>
              <Typography variant="body1" className={classes.itemName}>
                {item.name}
              </Typography>
              {Number(item.quantity) > 1 && (
                <Typography variant="body2" color="textSecondary">
                  {t('checkoutScreen.ticket.items.quantity')} {item.quantity}
                </Typography>
              )}
            </div>
            <Typography variant="body1" className={classes.itemPrice}>
              {formattedAmount(item.total, currency)}
            </Typography>
          </div>
        ))}
      </div>

      <Divider className={classes.divider} />

      <div className={classes.total}>
        <Typography variant="subtitle1" className={classes.totalLabel}>
          {t('checkoutScreen.payment.methods.spei.dlocal.bbvaBankTab.amount')}
        </Typography>
        <Typography variant="h6" className={classes.totalAmount}>
          {formattedAmount(total, currency)}
        </Typography>
      </div>

      <Typography variant="caption" color="textSecondary" className={classes.createdAt}>
        {`Link creado el ${new Date(
          links.paymentLink.createdAt,
        ).toLocaleDateString()} a las ${new Date(
          links.paymentLink.createdAt,
        ).toLocaleTimeString()}`}
      </Typography>
    </div>
  );
};

export default observer(OrderSummary);
